import React, { useState, useEffect } from 'react';
// @ts-ignore - Ignoring module import errors until packages are installed
import { Container, Row, Col, Card, Button, Form, Badge } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

// Demo data for exhibitions
const generateDemoData = () => {
  const semesters = ['Spring 2025', 'Fall 2024', 'Summer 2024', 'Spring 2024', 'Fall 2023'];
  const categories = ['Art & Design', 'Engineering', 'Computer Science', 'Architecture', 'Media Studies'];
  const titles = [
    'Interactive Media Installation', 'Sustainable Design Solutions', 'AI-Driven Art Generation',
    'Urban Planning Concepts', 'Virtual Reality Experiences', 'Robotics Showcase',
    'Digital Storytelling', 'Product Design Innovations', 'Data Visualization Projects',
    'Architectural Models', 'Game Development', 'Mobile App Prototypes'
  ];
  
  return Array.from({ length: 20 }, (_, i) => ({
    id: i + 1,
    title: titles[i % titles.length] + (i > titles.length ? ` ${Math.floor(i / titles.length)}` : ''),
    semester: semesters[i % semesters.length],
    category: categories[i % categories.length],
    students: [`Student ${i + 1}`, `Student ${i + 2}`, `Student ${i + 3}`],
    description: `This project showcases the creative and technical skills developed during the ${semesters[i % semesters.length]} semester. Students explored innovative approaches to ${categories[i % categories.length].toLowerCase()} through collaborative work and critical thinking.`,
    mediaType: i % 3 === 0 ? 'video' : 'image',
    mediaUrls: i % 3 === 0 ? 
      [`https://example.com/video${i + 1}.mp4`] : 
      Array.from({ length: Math.floor(Math.random() * 4) + 2 }, (_, j) => `https://picsum.photos/800/500?random=${i * 10 + j}`),
    date: new Date(2023 + Math.floor(i / 5), (i * 2) % 12, ((i + 1) * 3) % 28 + 1).toISOString().split('T')[0],
    featured: i < 3
  }));
};

interface Exhibition {
  id: number;
  title: string;
  semester: string;
  category: string;
  students: string[];
  description: string;
  mediaType: 'video' | 'image';
  mediaUrls: string[];
  date: string;
  featured: boolean;
}

const Exhibition = () => {
  const [exhibitions, setExhibitions] = useState<Exhibition[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSemester, setSelectedSemester] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedExhibition, setSelectedExhibition] = useState<Exhibition | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  
  const itemsPerPage = 6;
  
  // Load demo data
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const demoData = generateDemoData() as Exhibition[];
      setExhibitions(demoData);
      setLoading(false);
    }, 800);
  }, []);
  
  // Filter exhibitions based on selected filters
  const filteredExhibitions = exhibitions.filter(exhibition => {
    const semesterMatch = selectedSemester ? exhibition.semester === selectedSemester : true;
    const categoryMatch = selectedCategory ? exhibition.category === selectedCategory : true;
    return semesterMatch && categoryMatch;
  });
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredExhibitions.length / itemsPerPage);
  const currentExhibitions = filteredExhibitions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  // Get unique semesters and categories for filters
  const uniqueSemesters = Array.from(new Set(exhibitions.map(item => item.semester)));
  const uniqueCategories = Array.from(new Set(exhibitions.map(item => item.category)));
  
  // Handle pagination
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };
  
  // Handle exhibition selection
  const viewExhibitionDetails = (exhibition: Exhibition) => {
    setSelectedExhibition(exhibition);
    window.scrollTo(0, 0);
  };
  
  // Handle back to list
  const backToList = () => {
    setSelectedExhibition(null);
    setPreviewImage(null);
  };
  
  // Handle image preview
  const openImagePreview = (imageUrl: string) => {
    setPreviewImage(imageUrl);
  };
  
  // Close image preview
  const closeImagePreview = () => {
    setPreviewImage(null);
  };
  
  return (
    <div className="exhibition-page">
      {/* Hero Section */}
      <div className="hero-section bg-gradient-primary text-white py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={7}>
              <h1 className="display-4 fw-bold mb-3">Student Exhibitions</h1>
              <p className="lead fs-4 mb-4">
                Discover the creative achievements and innovative projects from our talented students across various disciplines and semesters.
              </p>
              <div className="d-flex gap-2 mb-3">
                <Badge bg="light" text="dark" className="px-3 py-2 fs-6">
                  <i className="fa fa-calendar-alt me-2"></i> Semester Showcases
                </Badge>
                <Badge bg="light" text="dark" className="px-3 py-2 fs-6">
                  <i className="fa fa-user-graduate me-2"></i> Student Work
                </Badge>
                <Badge bg="light" text="dark" className="px-3 py-2 fs-6">
                  <i className="fa fa-images me-2"></i> Visual Projects
                </Badge>
              </div>
            </Col>
            <Col lg={5} className="d-none d-lg-block">
              <img 
                src="https://picsum.photos/600/400?random=1" 
                alt="Exhibition Banner" 
                className="img-fluid rounded-3 shadow-lg" 
              />
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        {selectedExhibition ? (
          /* Exhibition Detail View */
          <div className="exhibition-detail">
            <Button 
              variant="outline-primary" 
              className="mb-4"
              onClick={backToList}
            >
              <i className="fa fa-arrow-left me-2"></i> Back to Exhibitions
            </Button>
            
            <Row>
              <Col lg={8}>
                <div className="media-gallery mb-4">
                  {selectedExhibition.mediaType === 'video' ? (
                    <div className="video-container ratio ratio-16x9 rounded shadow">
                      <video 
                        controls 
                        poster="https://picsum.photos/800/450?random=10"
                        className="rounded"
                      >
                        <source src={selectedExhibition.mediaUrls[0]} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  ) : (
                    <div className="image-gallery">
                      <Row>
                        {selectedExhibition.mediaUrls.map((url, index) => (
                          <Col md={index === 0 ? 12 : 6} className="mb-3" key={index}>
                            <div 
                              className="image-container position-relative cursor-pointer" 
                              onClick={() => openImagePreview(url)}
                            >
                              <img 
                                src={url} 
                                alt={`${selectedExhibition.title} - Image ${index + 1}`} 
                                className="img-fluid rounded shadow w-100" 
                                style={{ objectFit: 'cover', height: index === 0 ? '400px' : '200px' }}
                              />
                              <div className="image-overlay rounded d-flex align-items-center justify-content-center">
                                <div className="overlay-content text-white">
                                  <i className="fa fa-search-plus fa-2x"></i>
                                  <p className="mt-2 mb-0">Click to enlarge</p>
                                </div>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </div>
              </Col>
              
              <Col lg={4}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h2 className="mb-3">{selectedExhibition.title}</h2>
                    
                    <div className="d-flex align-items-center mb-2">
                      <i className="fa fa-calendar-alt text-muted me-2"></i>
                      <span className="text-muted">{selectedExhibition.semester}</span>
                    </div>
                    
                    <div className="d-flex align-items-center mb-2">
                      <i className="fa fa-tags text-muted me-2"></i>
                      <span className="text-muted">{selectedExhibition.category}</span>
                    </div>
                    
                    <div className="d-flex align-items-center mb-3">
                      <i className="fa fa-user-graduate text-muted me-2"></i>
                      <span className="text-muted">{selectedExhibition.students.join(', ')}</span>
                    </div>
                    
                    <hr />
                    
                    <h5>About this Project</h5>
                    <p className="text-muted">{selectedExhibition.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        ) : (
          /* Exhibition List View */
          <>
            {/* Filters */}
            <Row className="mb-4">
              <Col md={6} lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Filter by Semester</strong></Form.Label>
                  <Form.Select 
                    value={selectedSemester}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setSelectedSemester(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Semesters</option>
                    {uniqueSemesters.map((semester, index) => (
                      <option key={index} value={semester}>{semester}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6} lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Filter by Category</strong></Form.Label>
                  <Form.Select
                    value={selectedCategory}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setSelectedCategory(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Categories</option>
                    {uniqueCategories.map((category, index) => (
                      <option key={index} value={category}>{category}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            {/* Loading State */}
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading exhibitions...</p>
              </div>
            ) : (
              <>
                {/* Exhibition Cards */}
                {currentExhibitions.length > 0 ? (
                  <Row>
                    {currentExhibitions.map((exhibition) => (
                      <Col md={6} lg={4} className="mb-4" key={exhibition.id}>
                        <Card className="h-100 border-0 shadow-sm hover-card">
                          <div className="position-relative">
                            {exhibition.mediaType === 'video' ? (
                              <div className="ratio ratio-16x9">
                                <div className="d-flex align-items-center justify-content-center bg-dark">
                                  <img 
                                    src="https://picsum.photos/400/225?random=20" 
                                    alt="Video thumbnail" 
                                    className="img-fluid w-100" 
                                    style={{ objectFit: 'cover', opacity: '0.7' }}
                                  />
                                  <div className="position-absolute">
                                    <i className="fa fa-video text-white" aria-hidden="true"></i>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <img 
                                src={exhibition.mediaUrls[0]} 
                                className="card-img-top" 
                                alt={exhibition.title}
                                style={{ height: '200px', objectFit: 'cover' }}
                              />
                            )}
                            {exhibition.featured && (
                              <span className="position-absolute top-0 end-0 badge bg-warning m-2">
                                Featured
                              </span>
                            )}
                          </div>
                          
                          <Card.Body>
                            <div className="d-flex justify-content-between mb-2">
                              <Badge bg="primary">{exhibition.semester}</Badge>
                              <small className="text-muted">{exhibition.date}</small>
                            </div>
                            
                            <h5 className="card-title">{exhibition.title}</h5>
                            
                            <p className="card-text text-muted small mb-3">
                              {exhibition.description.substring(0, 100)}...
                            </p>
                            
                            <div className="d-flex justify-content-between align-items-center">
                              <Badge bg="light" text="dark">
                                <i className="fa fa-tags me-1"></i> {exhibition.category}
                              </Badge>
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => viewExhibitionDetails(exhibition)}
                              >
                                View Details
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="text-center py-5">
                    <h4>No exhibitions found matching your filters</h4>
                    <Button 
                      variant="primary" 
                      className="mt-3"
                      onClick={() => {
                        setSelectedSemester('');
                        setSelectedCategory('');
                      }}
                    >
                      Clear Filters
                    </Button>
                  </div>
                )}
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-4">
                    <nav>
                      <ul className="pagination">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <Button 
                            variant="link" 
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </Button>
                        </li>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <li 
                            key={page} 
                            className={`page-item ${currentPage === page ? 'active' : ''}`}
                          >
                            <Button 
                              variant={currentPage === page ? 'primary' : 'link'}
                              className="page-link"
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </Button>
                          </li>
                        ))}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <Button 
                            variant="link" 
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </Button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Container>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="image-preview-modal" onClick={closeImagePreview}>
          <div className="modal-content">
            <button className="close-button" onClick={closeImagePreview}>
              &times;
            </button>
            <img 
              src={previewImage} 
              alt="Preview" 
              className="preview-image" 
            />
          </div>
        </div>
      )}
      
      {/* Custom CSS */}
      <style>
        {`
        .bg-gradient-primary {
          background: linear-gradient(135deg, #4a6bff 0%, #6a3aef 100%);
        }
        
        .hover-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
        }
        
        .cursor-pointer {
          cursor: pointer;
        }
        
        .image-container {
          overflow: hidden;
        }
        
        .image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .image-container:hover .image-overlay {
          opacity: 1;
        }
        
        .overlay-content {
          text-align: center;
        }
        
        /* Image Preview Modal */
        .image-preview-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }
        
        .modal-content {
          position: relative;
          max-width: 90%;
          max-height: 90%;
        }
        
        .preview-image {
          max-width: 100%;
          max-height: 90vh;
          object-fit: contain;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
        }
        
        .close-button {
          position: absolute;
          top: -40px;
          right: -40px;
          background: transparent;
          border: none;
          color: white;
          font-size: 30px;
          cursor: pointer;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.3s ease;
        }
        
        .close-button:hover {
          background-color: rgba(255, 255, 255, 0.2);
        }
        `}
      </style>
    </div>
  );
};

export default Exhibition;
